import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, TextField, Button, Typography, Paper, Grid,
  Alert, CircularProgress, MenuItem, Card, CardContent
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { offerService, contractProcessingService } from '../services/api';
import OfferUpload from './OfferUpload';
import { usePermissions } from './PermissionsContext';

function OfferForm({ embedded=false, offerId=null, disabled: disabledProp=false, onSaved, onCancel }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const routeId = id;
  const { role } = usePermissions();

  // HRManager can only view, not edit (read-only mode)
  const isHRManagerReadOnly = role === 'HRManager';
  const disabled = disabledProp || isHRManagerReadOnly;

  const effectiveId = embedded ? offerId : routeId;
  const isEdit = embedded ? Boolean(effectiveId) : (Boolean(effectiveId) && window.location.pathname.includes('/edit'));
  const isView = embedded ? false : (Boolean(effectiveId) && !window.location.pathname.includes('/edit') && !window.location.pathname.includes('/new'));

  const [offer, setOffer] = useState({
    offerNumber: '',
    offerDate: '',
    candidateFullName: '',
    offeredPosition: '',
    offeredStartDate: '',
    packageDetails: '',
    packageStandardAddition: '',
    offerStatus: 'Pending',
    createdBy: 'Current User'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchOffer = useCallback(async () => {
    if (!effectiveId) return;

    try {
      setLoading(true);
      const offerData = await offerService.getById(effectiveId);

      setOffer({
        ...offerData,
        offerDate: offerData.offerDate ? offerData.offerDate.split('T')[0] : '',
        offeredStartDate: offerData.offeredStartDate ? offerData.offeredStartDate.split('T')[0] : ''
      });
    } catch (error) {
      setError('Error fetching offer details');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [effectiveId]);

  useEffect(() => {
    if (effectiveId) {
      fetchOffer();
    }
  }, [effectiveId, fetchOffer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOffer(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const prepareOfferForApi = (offer) => {
    const { linkedContract, ...cleanOffer } = offer; // Remove linkedContract
    return {
      ...cleanOffer,
      packageDetails: offer.packageDetails || '',
      packageStandardAddition: offer.packageStandardAddition || '',
      offerFilePath: offer.offerFilePath || cleanOffer.offerFilePath || ''
    };
  };

  const formatTextToHtml = (text) => {
    if (!text) return '';
    
    console.log('Input text:', JSON.stringify(text));
    
    // First, split by bullet points if they exist
    if (text.includes('•')) {
      const bulletPoints = text
        .split('•')
        .map(point => point.trim())
        .filter(point => point.length > 0);
      
      console.log('Split by bullets:', bulletPoints);
      
      const listItems = bulletPoints.map(point => `<li>${point}</li>`).join('');
      const finalResult = `<ul>${listItems}</ul>`;
      
      console.log('Final HTML:', finalResult);
      return finalResult;
    }
    
    // Fallback: split by sentences if no bullets
    const sentences = text
      .split(/\.\s+|\.$/)
      .map(sentence => sentence.trim())
      .filter(sentence => sentence.length > 0);
    
    if (sentences.length === 0) return '';
    
    const listItems = sentences.map(sentence => `<li>${sentence}</li>`).join('');
    const finalResult = `<ul>${listItems}</ul>`;
    
    console.log('Final HTML:', finalResult);
    return finalResult;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = prepareOfferForApi(offer);
      console.log('Sending payload:', JSON.stringify(payload, null, 2));
      
      if (isEdit) {
        await offerService.update(effectiveId, payload);
        setSuccess('Offer updated successfully!');
        // Don't navigate - stay on form for updates
      } else {
        const created = await offerService.create(payload);
        setSuccess('Offer created successfully!');
        if (!embedded) {
          setTimeout(() => { navigate('/offers'); }, 1000);
        } else {
          onSaved?.(created);
        }
      }
    } catch (error) {
      console.error('Full error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Validation errors:', error.response?.data?.errors);
      setError('Error saving offer');
    } finally {
      setLoading(false);
    }
  };

  const handleFileProcessed = (extractedData) => {
    // Convert escaped newlines to actual newlines, or split by bullets if no newlines
    let formattedPackageDetails = '';
    if (extractedData.packageDetails) {
      if (extractedData.packageDetails.includes('\\n')) {
        // Has escaped newlines - convert them
        formattedPackageDetails = extractedData.packageDetails.replace(/\\n/g, '\n');
      } else if (extractedData.packageDetails.includes('•')) {
        // No newlines but has bullets - split and add newlines
        formattedPackageDetails = extractedData.packageDetails
          .split('•')
          .filter(part => part.trim()) // Remove empty parts
          .map(part => `• ${part.trim()}`) // Add bullet back with proper spacing
          .join('\n'); // Join with newlines
      } else {
        // No special formatting needed
        formattedPackageDetails = extractedData.packageDetails;
      }
    }
    
    const formattedPackageStandardAddition = extractedData.packageStandardAddition
      ? extractedData.packageStandardAddition.replace(/\\n/g, '\n')
      : '';

    setOffer(prev => ({
      ...prev,
      ...extractedData,
      packageDetails: formattedPackageDetails,
      packageStandardAddition: formattedPackageStandardAddition,
      offerDate: extractedData.offerDate ? extractedData.offerDate.split('T')[0] : prev.offerDate,
      offeredStartDate: extractedData.offeredStartDate ? extractedData.offeredStartDate.split('T')[0] : prev.offeredStartDate
    }));
    setSuccess('Offer data extracted successfully from file!');
  };

  if (loading && (isEdit || isView)) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isView ? 'View Offer' : isEdit ? 'Edit Offer' : 'Create New Offer'}
      </Typography>

      {!isView && !isEdit && !disabled && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Upload Offer Document
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload a PDF or image of the employment offer to automatically extract data
            </Typography>
            <OfferUpload onFileProcessed={handleFileProcessed} />
          </CardContent>
        </Card>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Offer Number"
                name="offerNumber"
                value={offer.offerNumber}
                onChange={handleChange}
                required
                disabled={isView || disabled}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Offer Date"
                name="offerDate"
                type="date"
                value={offer.offerDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
                disabled={isView || disabled}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Candidate Full Name"
                name="candidateFullName"
                value={offer.candidateFullName}
                onChange={handleChange}
                required
                disabled={isView || disabled}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Offered Position"
                name="offeredPosition"
                value={offer.offeredPosition}
                onChange={handleChange}
                required
                disabled={isView || disabled}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Offered Start Date"
                name="offeredStartDate"
                type="date"
                value={offer.offeredStartDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
                disabled={isView || disabled}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Offer Status"
                name="offerStatus"
                value={offer.offerStatus}
                onChange={handleChange}
                disabled={isView || disabled}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Accepted">Accepted</MenuItem>
                <MenuItem value="Declined">Declined</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sx={{ width: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Package Details
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  minHeight: 200,
                  bgcolor: '#fafafa',
                  '& ul': { 
                    paddingLeft: 2, 
                    margin: 0,
                    listStyleType: 'disc'
                  },
                  '& li': { 
                    marginBottom: 1,
                    lineHeight: 1.6
                  },
                  '& p': { 
                    margin: 0, 
                    marginBottom: 1,
                    lineHeight: 1.6
                  }
                }}
              >
                {offer.packageDetails ? (
                  <Box dangerouslySetInnerHTML={{ __html: formatTextToHtml(offer.packageDetails) }} />
                ) : (
                  <Typography color="text.secondary" fontStyle="italic">
                    No package details available
                  </Typography>
                )}
              </Paper>
            </Grid>

            {!isView && !disabled && (
              <Grid item xs={12}>
                <Box display="flex" gap={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : (isEdit ? 'Update' : 'Create')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => { if (!embedded) navigate('/offers'); else onCancel?.(); }}
                  >
                    {isEdit ? 'Close' : 'Cancel'}
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}

export default OfferForm;




























